import React, { useCallback, useEffect, useRef, useState } from 'react';
import './ScrollHintRow.css';

/**
 * iter330 — ScrollHintRow
 *
 * Wraps any horizontally-scrollable row of chips/cards and adds:
 *   1. Soft gradient fade edges on the left + right, visible only
 *      when there's actually more content to reveal in that
 *      direction. So a fully-visible row looks pristine, while a
 *      row that overflows gets a clear "more →" cue.
 *   2. A one-time gentle nudge animation on first mount of the
 *      session — the track scrolls a few pixels in the reading
 *      direction, then back, so the user sees the row is
 *      scrollable. Persisted via sessionStorage so it doesn't
 *      repeat on every page navigation.
 *   3. Respects `prefers-reduced-motion`: nudge is skipped, but the
 *      static gradient + arrows remain so the affordance is still
 *      communicated visually.
 *
 * Usage:
 *   <ScrollHintRow storageKey="shop-cat-rail" testid="...">
 *     <div className="catRail__grid">...</div>
 *   </ScrollHintRow>
 *
 * The wrapped element must be horizontally scrollable
 * (`overflow-x: auto`) — this component does NOT add scroll
 * behavior, it only decorates an existing scroll container.
 *
 * RTL-safe via `dir` inheritance + logical CSS properties.
 */
export default function ScrollHintRow({
  children,
  className = '',
  storageKey,
  testid = 'scroll-hint-row',
  nudgeAmount = 56,
  nudgeDelay = 700,
}) {
  const wrapRef = useRef(null);
  const [canStart, setCanStart] = useState(false); // can scroll towards start
  const [canEnd, setCanEnd] = useState(false);     // can scroll towards end

  // Find the actual scroll container — it's the first child element
  // (the wrapped <div> the caller passed in).
  const getScrollEl = useCallback(() => {
    const wrap = wrapRef.current;
    if (!wrap) return null;
    // The component renders an inner shell so we always know exactly
    // which element to query. See JSX below.
    return wrap.querySelector('[data-scroll-hint-track]');
  }, []);

  const updateEdgeState = useCallback(() => {
    const el = getScrollEl();
    if (!el) return;
    const isRTL = (document?.documentElement?.dir || 'ltr') === 'rtl';

    // Some browsers (Firefox, WebKit) report negative scrollLeft in
    // RTL when the user has scrolled towards the visual end. Abs()
    // covers all modes.
    const scrollLeft = Math.abs(el.scrollLeft);
    const maxScroll = el.scrollWidth - el.clientWidth;

    const atStart = scrollLeft <= 4;
    const atEnd = scrollLeft >= maxScroll - 4;
    const hasOverflow = maxScroll > 4;

    if (!hasOverflow) {
      setCanStart(false);
      setCanEnd(false);
      return;
    }

    // Map "logical start/end" to visual sides. In LTR: start=left
    // edge; in RTL: start=right edge.
    setCanStart(!atStart);
    setCanEnd(!atEnd);

    // Silence "isRTL unused" — keep around for future per-side gradients.
    void isRTL;
  }, [getScrollEl]);

  useEffect(() => {
    const el = getScrollEl();
    if (!el) return undefined;
    updateEdgeState();
    el.addEventListener('scroll', updateEdgeState, { passive: true });
    const ro = new ResizeObserver(updateEdgeState);
    ro.observe(el);
    window.addEventListener('resize', updateEdgeState);
    return () => {
      el.removeEventListener('scroll', updateEdgeState);
      ro.disconnect();
      window.removeEventListener('resize', updateEdgeState);
    };
  }, [getScrollEl, updateEdgeState]);

  // One-time nudge per session. Only runs if:
  //   • prefers-reduced-motion is OFF
  //   • the track is actually overflowing
  //   • the sessionStorage flag hasn't already been set
  useEffect(() => {
    if (!storageKey) return undefined;

    const mq = typeof window !== 'undefined'
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null;
    if (mq && mq.matches) return undefined;

    let storageOk = false;
    try {
      storageOk = !sessionStorage.getItem(storageKey);
    } catch {
      // sessionStorage blocked (Safari private). Show the nudge once
      // per mount — better than silently giving up.
      storageOk = true;
    }
    if (!storageOk) return undefined;

    const el = getScrollEl();
    if (!el) return undefined;
    const maxScroll = el.scrollWidth - el.clientWidth;
    if (maxScroll <= 4) return undefined; // no overflow → no nudge.

    const isRTL = (document?.documentElement?.dir || 'ltr') === 'rtl';
    const sign = isRTL ? -1 : 1;
    let nudgedAt = null;

    const t1 = setTimeout(() => {
      try {
        el.scrollBy({ left: sign * nudgeAmount, behavior: 'smooth' });
        nudgedAt = Date.now();
      } catch {
        // Older Safari without scroll options — set scrollLeft directly.
        el.scrollLeft = el.scrollLeft + sign * nudgeAmount;
      }
    }, nudgeDelay);

    const t2 = setTimeout(() => {
      try {
        el.scrollBy({ left: -sign * nudgeAmount, behavior: 'smooth' });
      } catch {
        el.scrollLeft = el.scrollLeft - sign * nudgeAmount;
      }
      try { sessionStorage.setItem(storageKey, '1'); } catch { /* noop */ }
    }, nudgeDelay + 650);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      void nudgedAt;
    };
  }, [storageKey, getScrollEl, nudgeAmount, nudgeDelay]);

  // We clone the single child to inject `data-scroll-hint-track` so
  // the wrapped element keeps its existing classNames and styling.
  const child = React.Children.only(children);
  const tracked = React.cloneElement(child, {
    'data-scroll-hint-track': true,
  });

  return (
    <div
      ref={wrapRef}
      className={`scroll-hint-row ${className}`.trim()}
      data-can-start={canStart || undefined}
      data-can-end={canEnd || undefined}
      data-testid={testid}
    >
      <span className="scroll-hint-edge scroll-hint-edge-start" aria-hidden="true" />
      <span className="scroll-hint-edge scroll-hint-edge-end" aria-hidden="true" />
      {tracked}
    </div>
  );
}
